import { chromeHasKeyboardFocus } from '../shared/ui/chrome-focus';
import {
  $,
  api,
  appState,
  readStoredRemoteStreamSettings,
  saveRemoteStreamSettings,
  saveStatsOverlay,
} from './app-state';
import {
  clearPendingRemoteClipboard,
} from './clipboard';
import {
  resetFileTransfers,
  setFileDropActive,
  updateFileTransferButton,
} from './file-transfer';
import {
  releaseAllMods,
  updateShortcutButton,
} from './keybindings';
import {
  disconnectedWebRtcPath,
  formatWebRtcConnectionMetrics,
  formatWebRtcLiveStats,
  formatWebRtcTraffic,
  pendingWebRtcPath,
  type WebRtcConnectionPath,
  type WebRtcConnectionMetrics,
} from '../shared/rtc-path';
import {
  normalizeRemoteStreamSettings,
  type RemoteStreamSettings,
} from '../shared/stream-profile';
import type { VideoSettingsAckMessage } from '../shared/datachannel';
import { normalizeSessionAccessMode, type SessionAccessMode } from '../shared/session-mode';
import { showGuestWaiting, hideGuestWaiting } from '../shared/ui/guest-waiting';
import { closeTextSendLayer } from './text-send';
import {
  resetConnectionQualityPanel,
  setConnectionVideoResolution,
  setConnectionQualityPanelOpen,
  toggleConnectionQualityPanel,
  updateConnectionQualityPanel,
} from '../shared/ui/connection-quality';
import { formatSessionSupportDiagnostics } from '../shared/support-diagnostics';

const SESSION_CHROME_IDLE_MS = 2000;
const SESSION_CHROME_LEAVE_MS = 120;
let sessionChromeTimer: number | null = null;
let sessionChromeBound = false;
let sessionChromePointerInside = false;
let sessionChromeAutoHideArmed = false;
let latestSessionMetrics: WebRtcConnectionMetrics | null = null;
let latestSessionPath = '正在建立连接';
let activeView: 'connect' | 'session' = 'connect';
let viewChangeRequest = 0;

function clearSessionChromeHideTimer(): void {
  if (sessionChromeTimer === null) return;
  window.clearTimeout(sessionChromeTimer);
  sessionChromeTimer = null;
}

function sessionIsVisible(): boolean {
  return !$('view-session').classList.contains('hidden');
}

function focusViewEntry(name: 'connect' | 'session'): void {
  const target = name === 'session' ? appState.video : document.getElementById('guest-code');
  if (!(target instanceof HTMLElement) || target.hasAttribute('disabled')) return;
  target.focus({ preventScroll: true });
}

function showView(name: 'connect' | 'session'): void {
  const previous = activeView;
  const request = ++viewChangeRequest;
  activeView = name;
  const connect = $('view-connect');
  const session = $('view-session');
  connect.classList.toggle('hidden', name !== 'connect');
  session.classList.toggle('hidden', name !== 'session');
  connect.setAttribute('aria-hidden', String(name !== 'connect'));
  session.setAttribute('aria-hidden', String(name !== 'session'));
  document.body.classList.toggle('in-session', name === 'session');
  if (name !== previous) {
    // 先让视图完成显隐和布局，再恢复入口焦点；同步 focus 会在手机浏览器中
    // 触发一次强制布局，连接页与会话页快速切换时尤其容易产生滚动跳动。
    window.requestAnimationFrame(() => {
      if (request === viewChangeRequest && activeView === name) focusViewEntry(name);
    });
  }
  if (name === 'connect') {
    clearSessionChromeHideTimer();
    sessionChromePointerInside = false;
    sessionChromeAutoHideArmed = false;
    appState.sessionStalled = false;
    $('view-session').classList.remove('session-stalled');
    closeStreamSettings(false);
  }
  if (name === 'session' && appState.devicesRefreshTimer !== null) {
    window.clearTimeout(appState.devicesRefreshTimer);
    appState.devicesRefreshTimer = null;
  }
}

function updateConnectionPath(info: WebRtcConnectionPath): void {
  latestSessionPath = info.label;
  const path = $('session-path');
  path.textContent = info.label;
  path.title = info.label;
  path.setAttribute('aria-label', info.label);
  path.dataset.path = info.kind;
  const route = document.getElementById('session-quality-route');
  if (route) route.textContent = info.label;
}

function updateConnectionQuality(metrics: WebRtcConnectionMetrics): void {
  latestSessionMetrics = { ...metrics };
  updateConnectionTraffic(metrics);
  updateConnectionLiveStats(metrics);
  updateConnectionQualityPanel(metrics);
  const quality = $('session-quality');
  quality.textContent = formatWebRtcConnectionMetrics(metrics);
  quality.title = quality.textContent || '正在检查连接状态';
  quality.dataset.quality =
    metrics.packetLossPercent != null && metrics.packetLossPercent >= 5 ? 'warn' : 'normal';
}

function setSessionAccessMode(mode: SessionAccessMode): void {
  const normalized = normalizeSessionAccessMode(mode);
  appState.sessionMode = normalized;
  const root = $('view-session');
  root.dataset.mode = normalized;
  const viewOnly = normalized === 'view';
  const setText = (id: string, text: string): void => {
    const element = document.getElementById(id);
    if (element) element.textContent = text;
  };
  setText('session-mode-label', viewOnly ? '仅观看' : '正在控制');
  setText('session-desktop-hint', viewOnly ? '只接收远端画面' : '可以直接使用鼠标和键盘');
  setText('session-side-title', viewOnly ? '正在共享屏幕' : '直接操作另一台电脑');
  setText('session-side-copy', viewOnly
    ? '这是仅观看会话。画面会持续更新，但不会发送鼠标、键盘或文件操作。'
    : '点击画面后，就可以使用鼠标、滚轮和键盘。');
  appState.video?.setAttribute(
    'aria-label',
    viewOnly ? '远程屏幕，仅观看模式' : '远程屏幕，可使用鼠标、键盘和触控操作',
  );
  if (viewOnly) {
    appState.pointerInput?.release();
    appState.keyboardInput?.release();
    releaseAllMods();
  }
}

function updateConnectionTraffic(metrics: WebRtcConnectionMetrics): void {
  const traffic = $('session-traffic');
  const text = formatWebRtcTraffic(metrics);
  traffic.textContent = text;
  traffic.title = text;
  traffic.setAttribute('aria-label', text);
  traffic.dataset.traffic =
    metrics.uploadKbps == null && metrics.downloadKbps == null ? 'unknown' : 'active';
}

function updateConnectionLiveStats(metrics: WebRtcConnectionMetrics): void {
  const text = formatWebRtcLiveStats(metrics);
  const toolbar = document.querySelector<HTMLElement>('.toolbar');
  let toolbarStats = document.getElementById('session-toolbar-stats');
  if (!toolbarStats && toolbar) {
    toolbarStats = document.createElement('span');
    toolbarStats.id = 'session-toolbar-stats';
    toolbarStats.className = 'session-toolbar-stats';
    toolbarStats.title = '实时连接信息';
    toolbarStats.setAttribute('aria-label', '实时连接信息：等待连接');
    toolbar.insertBefore(toolbarStats, toolbar.firstChild);
  }
  const toolbarText = `实时 · ${text}`;
  for (const element of [toolbarStats, document.getElementById('session-overlay-stats')]) {
    if (!element) continue;
    element.textContent = element === toolbarStats ? toolbarText : text;
    element.title = text;
    element.setAttribute('aria-label', `实时连接信息：${text}`);
  }
}

function setSessionStatsOverlayEnabled(enabled: boolean): void {
  appState.sessionStatsOverlay = enabled;
  const overlay = document.getElementById('session-stats-overlay');
  if (overlay) overlay.dataset.enabled = String(enabled);
}

function setSessionControlsHidden(hidden: boolean): void {
  appState.sessionControlsHidden = hidden;
  if (hidden) setConnectionQualityPanelOpen(false);
  const root = $('view-session');
  root.classList.toggle('controls-hidden', hidden);
  root.classList.toggle('session-chrome-hidden', hidden);
  const button = $('session-controls-toggle') as HTMLButtonElement;
  button.setAttribute('aria-expanded', String(!hidden));
  button.setAttribute('aria-label', hidden ? '显示控制栏' : '隐藏控制栏');
  button.title = hidden ? '显示控制栏' : '隐藏控制栏';
  for (const [id, label] of [
    ['session-top-reveal', '顶部控制栏'],
    ['session-bottom-reveal', '底部控制栏'],
  ] as const) {
    const edge = $(id) as HTMLButtonElement | null;
    if (!edge) continue;
    edge.setAttribute('aria-expanded', String(!hidden));
    edge.setAttribute('aria-label', hidden ? `显示${label}` : `隐藏${label}`);
    edge.title = hidden ? `显示${label}` : `隐藏${label}`;
  }
  if (hidden) {
    clearSessionChromeHideTimer();
    const active = document.activeElement;
    const top = $('view-session').querySelector('.sessbar');
    const toolbar = $('view-session').querySelector('.toolbar');
    if (active && (top?.contains(active) || toolbar?.contains(active))) {
      appState.video?.focus({ preventScroll: true });
    }
  } else if (
    sessionChromeBound &&
    sessionChromeAutoHideArmed &&
    !sessionChromePointerInside &&
    !appState.sessionStalled &&
    sessionIsVisible()
  ) {
    scheduleSessionChromeHide();
  }
}

function scheduleSessionChromeHide(delay = SESSION_CHROME_IDLE_MS): void {
  clearSessionChromeHideTimer();
  if (
    !sessionChromeBound ||
    !sessionChromeAutoHideArmed ||
    !sessionIsVisible() ||
    sessionChromePointerInside ||
    chromeHasKeyboardFocus($('view-session')) ||
    appState.sessionStalled
  ) return;
  sessionChromeTimer = window.setTimeout(() => {
    sessionChromeTimer = null;
    if (sessionIsVisible() && !sessionChromePointerInside && !chromeHasKeyboardFocus($('view-session'))) setSessionControlsHidden(true);
  }, delay);
}

function armSessionChromeAutoHide(): void {
  sessionChromeAutoHideArmed = true;
  scheduleSessionChromeHide();
}

function revealSessionChrome(): void {
  if (!sessionIsVisible()) return;
  setSessionControlsHidden(false);
}

function bindSessionChromeAutoHide(): void {
  if (sessionChromeBound) return;
  const root = $('view-session');
  const top = root.querySelector<HTMLElement>('.sessbar');
  const toolbar = root.querySelector<HTMLElement>('.toolbar');
  const topEdge = $('session-top-reveal');
  const bottomEdge = $('session-bottom-reveal');
  if (!top || !toolbar || !topEdge || !bottomEdge) return;

  sessionChromeBound = true;
  const chrome = [top, toolbar, topEdge, bottomEdge];
  const isInside = (event: PointerEvent, element: Element): boolean => {
    const rect = element.getBoundingClientRect();
    return event.clientX >= rect.left &&
      event.clientX <= rect.right &&
      event.clientY >= rect.top &&
      event.clientY <= rect.bottom;
  };
  const isEventInside = (event: PointerEvent, element: Element): boolean =>
    event.target instanceof Node && element.contains(event.target);
  const markInside = (inside: boolean): void => {
    sessionChromePointerInside = inside;
    if (inside) {
      clearSessionChromeHideTimer();
      revealSessionChrome();
    } else {
      // 给指针从横线进入工具栏留出极短的过渡时间；离开整个控制区后
      // 快速收起，不再等待完整的空闲计时。
      scheduleSessionChromeHide(SESSION_CHROME_LEAVE_MS);
    }
  };
  for (const element of chrome) {
    element.addEventListener('pointerenter', () => markInside(true));
    element.addEventListener('pointerleave', () => markInside(false));
    element.addEventListener('pointerdown', () => revealSessionChrome());
    element.addEventListener('focusin', () => { clearSessionChromeHideTimer(); revealSessionChrome(); });
    element.addEventListener('focusout', () => queueMicrotask(() => scheduleSessionChromeHide()));
  }
  for (const edge of [topEdge, bottomEdge]) {
    edge.addEventListener('click', () => revealSessionChrome());
  }
  root.addEventListener('pointermove', (event) => {
    // 只命中横杠本身才触发，不能用“距离上下边缘几十像素”扩大热区，
    // 否则用户在画面顶部/底部操作时会误展开工具栏。
    const overEdge = isInside(event, topEdge) || isInside(event, bottomEdge);
    const overChrome = chrome.some((element) => isEventInside(event, element));
    if (overEdge || overChrome) {
      if (!sessionChromePointerInside) markInside(true);
    } else if (!overChrome && sessionChromePointerInside) {
      markInside(false);
    } else if (!overChrome) {
      scheduleSessionChromeHide(SESSION_CHROME_LEAVE_MS);
    }
  });
  root.addEventListener('pointerleave', () => markInside(false));
  window.addEventListener('blur', () => markInside(false));
}

function resetSessionChrome(): void {
  clearSessionChromeHideTimer();
  sessionChromePointerInside = false;
  sessionChromeAutoHideArmed = false;
  setConnectionQualityPanelOpen(false);
  resetConnectionQualityPanel();
  appState.sessionStalled = false;
  $('view-session').classList.remove('session-stalled');
  setSessionControlsHidden(false);
  const status = $('session-status');
  status.dataset.phase = 'idle';
  status.dataset.reason = 'manual';
}

function setSessionConnectionStalled(stalled: boolean): void {
  appState.sessionStalled = stalled;
  $('view-session').classList.toggle('session-stalled', stalled);
  if (stalled) {
    clearSessionChromeHideTimer();
    revealSessionChrome();
  } else if (!appState.sessionControlsHidden && !sessionChromePointerInside) {
    scheduleSessionChromeHide();
  }
}

function streamQualityLabel(value: RemoteStreamSettings['quality']): string {
  if (value === '720p') return '省流量';
  if (value === '1080p') return '清晰';
  if (value === '1440p') return '更清晰';
  if (value === '2160p') return '极清晰';
  return '自动';
}

function streamFrameRateLabel(value: RemoteStreamSettings['frameRate']): string {
  if (value === 24) return '省电';
  if (value === 30) return '标准';
  if (value === 60) return '更流畅';
  return '自动';
}

function updateStreamSettingsButton(): void {
  const button = $('session-stream-settings') as HTMLButtonElement;
  const summary = `${streamQualityLabel(appState.remoteStreamSettings.quality)} · ${streamFrameRateLabel(appState.remoteStreamSettings.frameRate)}`;
  button.title = `画面设置：${summary}`;
  button.setAttribute('aria-label', `画面设置，当前${summary}`);
  const label = button.querySelector('[data-stream-settings-label]');
  if (label) label.textContent = '画面';
}

function setStreamSettingsNote(text: string, state: 'ok' | 'warn' | 'error' | '' = ''): void {
  const note = $('stream-settings-note');
  note.textContent = text;
  note.className = `stream-settings-note${state ? ` ${state}` : ''}`;
}

function closeStreamSettings(restoreFocus = true): void {
  const layer = $('stream-settings-layer');
  if (layer.classList.contains('hidden')) return;
  layer.classList.add('hidden');
  layer.setAttribute('aria-hidden', 'true');
  if (appState.streamSettingsTimer !== null) {
    window.clearTimeout(appState.streamSettingsTimer);
    appState.streamSettingsTimer = null;
  }
  if (restoreFocus) appState.streamSettingsTrigger?.focus({ preventScroll: true });
  appState.streamSettingsTrigger = null;
}

function openStreamSettings(): void {
  const layer = $('stream-settings-layer');
  appState.streamSettingsTrigger = $('session-stream-settings');
  const quality = $('stream-quality') as HTMLSelectElement;
  const frameRate = $('stream-framerate') as HTMLSelectElement;
  const statsOverlay = $('stream-stats-overlay') as HTMLInputElement;
  quality.value = appState.remoteStreamSettings.quality;
  frameRate.value = String(appState.remoteStreamSettings.frameRate);
  statsOverlay.checked = appState.sessionStatsOverlay;
  layer.classList.remove('hidden');
  layer.setAttribute('aria-hidden', 'false');
  setStreamSettingsNote('画面偏好会保存到本机，下次连接自动使用。当前会话也会立即尝试应用。');
  window.setTimeout(() => quality.focus({ preventScroll: true }), 0);
}

function trapStreamSettingsFocus(event: KeyboardEvent): void {
  const layer = $('stream-settings-layer');
  if (layer.classList.contains('hidden')) return;
  if (event.key === 'Escape') {
    event.preventDefault();
    closeStreamSettings();
    return;
  }
  if (event.key !== 'Tab') return;
  const focusable = Array.from(layer.querySelectorAll<HTMLElement>(
    'button:not([disabled]), select:not([disabled]), input:not([disabled]), textarea:not([disabled])',
  ));
  if (focusable.length === 0) return;
  const first = focusable[0];
  const last = focusable[focusable.length - 1];
  if (event.shiftKey && document.activeElement === first) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault();
    first.focus();
  }
}

function applyStreamSettings(): void {
  const quality = ($('stream-quality') as HTMLSelectElement).value;
  const rawFrameRate = ($('stream-framerate') as HTMLSelectElement).value;
  const statsOverlay = ($('stream-stats-overlay') as HTMLInputElement).checked;
  const settings = normalizeRemoteStreamSettings({
    quality,
    frameRate: rawFrameRate === 'auto' ? 'auto' : Number(rawFrameRate),
  });
  appState.remoteStreamSettings = settings;
  saveRemoteStreamSettings(settings);
  updateStreamSettingsButton();
  setSessionStatsOverlayEnabled(statsOverlay);
  saveStatsOverlay(statsOverlay);
  if (!appState.controller?.sendVideoSettings(settings)) {
    setStreamSettingsNote('已保存，连接建立后会自动应用。', 'ok');
    return;
  }
  setStreamSettingsNote('已保存，正在应用…');
  if (appState.streamSettingsTimer !== null) window.clearTimeout(appState.streamSettingsTimer);
  appState.streamSettingsTimer = window.setTimeout(() => {
    appState.streamSettingsTimer = null;
    setStreamSettingsNote('暂时没有收到确认，请稍后重试。', 'warn');
  }, 3500);
}

function updateAppliedStreamSettings(settings: VideoSettingsAckMessage): void {
  if (appState.streamSettingsTimer !== null) {
    window.clearTimeout(appState.streamSettingsTimer);
    appState.streamSettingsTimer = null;
  }
  if (!settings.ok) {
    setStreamSettingsNote('暂时无法调整画面，请稍后重试。', 'error');
    return;
  }
  setConnectionVideoResolution(settings.width, settings.height);
  const actual = [streamQualityLabel(settings.quality), streamFrameRateLabel(settings.frameRate)].join(' · ');
  setStreamSettingsNote(`已应用：${actual}`, 'ok');
  $('session-status').textContent = `${settings.automatic ? '画面已自动优化' : '画面设置已应用'} · ${actual}`;
}

async function copySessionDiagnostics(): Promise<void> {
  const status = document.getElementById('session-quality-copy-status');
  const connection = appState.controller?.getConnectionDiagnostics() || null;
  const version = document.querySelector<HTMLMetaElement>('meta[name="linkdesk-version"]')?.content || '未知';
  const summary = formatSessionSupportDiagnostics({
    client: 'Web',
    version,
    mode: appState.sessionMode,
    route: latestSessionPath,
    metrics: latestSessionMetrics,
    connection,
  });
  const copied = await api.writeClipboard(summary).catch(() => false);
  if (status) {
    status.textContent = copied ? '已复制，可粘贴给支持人员' : '复制失败，请稍后重试';
    status.dataset.state = copied ? 'ok' : 'error';
  }
  window.setTimeout(() => {
    if (!status) return;
    status.textContent = '';
    delete status.dataset.state;
  }, 3500);
}

function startSession(mode: SessionAccessMode = 'control', onRetry?: () => void): void {
  document.querySelectorAll<HTMLDialogElement>('dialog[open]').forEach(dialog => dialog.close());
  appState.controller?.dispose();
  appState.controller = null;
  appState.retrySession = onRetry || null;
  appState.cancelSession = () => disconnect('连接已取消，可重新连接。');
  resetFileTransfers();
  clearPendingRemoteClipboard();
  setSessionAccessMode(mode);
  showView('session');
  appState.sessionWasConnected = false;
  latestSessionMetrics = null;
  latestSessionPath = '正在建立连接';
  resetSessionChrome();
  $('session-status').textContent = '连接中…';
  updateConnectionPath(pendingWebRtcPath());
  updateConnectionQuality({
    rttMs: null,
    jitterMs: null,
    packetLossPercent: null,
    framesPerSecond: null,
    framesDropped: null,
    framesDecoded: null,
    jitterBufferMs: null,
    availableOutgoingBitrateKbps: null,
    qualityLimitationReason: null,
    controlBufferedAmount: null,
  });
  resetConnectionQualityPanel();
  appState.remotePlatform = 'unknown';
  appState.shortcutMode = 'auto';
  appState.remoteStreamSettings = readStoredRemoteStreamSettings();
  updateStreamSettingsButton();
  updateShortcutButton();
  appState.video?.focus({ preventScroll: true });
  // 所有连接入口都共享同一条“等待连接 / 取消返回”路径，避免账号、局域网和设备码
  // 在连接尚未建立时只剩一块没有下一步的黑屏。等待层最后挂载，焦点落在取消按钮上。
  showGuestWaiting(() => disconnect('连接已取消，可重新连接。'), onRetry);
}

function disconnect(notice = '连接已结束，可再次输入设备码。'): void {
  hideGuestWaiting();
  closeTextSendLayer(false);
  appState.pointerInput?.release();
  appState.keyboardInput?.release();
  releaseAllMods();
  appState.controller?.dispose();
  appState.controller = null;
  appState.retrySession = null;
  appState.cancelSession = null;
  if (appState.video?.srcObject) {
    (appState.video.srcObject as MediaStream).getTracks().forEach((t) => t.stop());
    appState.video.srcObject = null;
  }
  appState.remotePlatform = 'unknown';
  setSessionAccessMode('control');
  appState.sessionWasConnected = false;
  latestSessionMetrics = null;
  latestSessionPath = '未连接';
  appState.sessionStalled = false;
  appState.fileTransferBusy = false;
  appState.fileDragDepth = 0;
  setFileDropActive(false);
  updateFileTransferButton();
  resetFileTransfers();
  clearPendingRemoteClipboard();
  closeStreamSettings(false);
  appState.remoteStreamSettings = readStoredRemoteStreamSettings();
  updateStreamSettingsButton();
  resetSessionChrome();
  updateConnectionPath(disconnectedWebRtcPath());
  updateConnectionQuality({
    rttMs: null,
    jitterMs: null,
    packetLossPercent: null,
    framesPerSecond: null,
    framesDropped: null,
    framesDecoded: null,
    jitterBufferMs: null,
    availableOutgoingBitrateKbps: null,
    qualityLimitationReason: null,
    controlBufferedAmount: null,
  });
  updateShortcutButton();
  showView('connect');
  const guestNotice = $('guest-error');
  guestNotice.textContent = notice;
  guestNotice.className = 'status';
}

function syncSessionVideoVisibility(): void {
  if ($('view-session').classList.contains('hidden')) return;
  // 标签页切走只影响渲染可见性，不关闭接收轨道、不暂停 WebRTC。
  // 回来后主动恢复播放并触发一次连接健康检查。
  appState.controller?.setVideoEnabled(true);
  appState.controller?.recover();
  if (appState.video?.srcObject && appState.video.paused) {
    void appState.video.play().catch(() => {});
  }
}

export {
  showView,
  updateConnectionPath,
  updateConnectionQuality,
  updateConnectionLiveStats,
  setSessionStatsOverlayEnabled,
  setSessionControlsHidden,
  updateStreamSettingsButton,
  armSessionChromeAutoHide,
  bindSessionChromeAutoHide,
  revealSessionChrome,
  setSessionConnectionStalled,
  closeStreamSettings,
  openStreamSettings,
  trapStreamSettingsFocus,
  applyStreamSettings,
  updateAppliedStreamSettings,
  copySessionDiagnostics,
  setSessionAccessMode,
  setConnectionQualityPanelOpen,
  toggleConnectionQualityPanel,
  startSession,
  disconnect,
  syncSessionVideoVisibility,
};
